import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { RevealLines } from "./Reveal";
import { resolveImg } from "@/lib/api";
import { ArrowDown } from "lucide-react";

export default function Hero({ hero = {} }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.18]);
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.55, 0.85]);

  return (
    <section
      id="hero"
      ref={ref}
      data-testid="hero-section"
      className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-stone-950"
    >
      {/* Background image with parallax */}
      <motion.div className="absolute inset-0" style={{ scale, y }}>
        <img
          src={resolveImg(hero.backgroundImage)}
          alt="Ambiente do restaurante"
          className="h-full w-full object-cover"
        />
      </motion.div>
      <motion.div
        className="absolute inset-0 bg-stone-950"
        style={{ opacity: overlayOpacity }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-stone-950 via-stone-950/20 to-stone-950/40" />

      {/* Content */}
      <div className="relative z-10 h-full max-w-7xl mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 md:pb-28">
        <motion.span
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="text-xs tracking-[0.35em] uppercase text-amber-500 font-semibold mb-6"
        >
          {hero.eyebrow || "Bem-vindo"}
        </motion.span>

        <h1 className="font-serif text-stone-50 text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.95] max-w-4xl">
          <RevealLines text={hero.headline} delay={0.25} />
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.9 }}
          className="mt-8 max-w-xl text-base lg:text-lg text-stone-300 leading-relaxed"
        >
          {hero.subheadline}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.05 }}
          className="mt-10"
        >
          <a
            href={hero.ctaUrl || "#cardapio"}
            data-testid="hero-cta"
            className="inline-flex items-center gap-3 rounded-full bg-stone-50 text-stone-950 px-8 py-4 font-medium hover:bg-amber-500 hover:text-stone-950 transition-colors duration-300"
          >
            {hero.ctaLabel || "Ver Cardápio"}
            <ArrowDown className="h-4 w-4" />
          </a>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 flex items-center gap-3 text-stone-400 text-xs tracking-[0.2em] uppercase"
      >
        <span>Role para explorar</span>
        <span className="h-px w-10 bg-stone-600" />
      </motion.div>
    </section>
  );
}
